import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  initUser,
  getStoredUserId,
  getWallet,
  listTransactions,
  listLedger,
  topupInit,
  verifyReference,
} from "@/lib/afritask";

const TOPUP_TIERS = [
  { amount: 1000, points: 100, badge: null },
  { amount: 5000, points: 550, badge: "+10%" },
  { amount: 10000, points: 1200, badge: "+20%" },
  { amount: 25000, points: 3250, badge: "+30%" },
];

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function POC() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [me, setMe] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [busyAmount, setBusyAmount] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const refreshAll = useCallback(async () => {
    const [w, t, l] = await Promise.all([
      getWallet(),
      listTransactions(),
      listLedger(),
    ]);
    setWallet(w);
    setTransactions(t);
    setLedger(l);
  }, []);

  // Bootstrap: create/load anonymous user
  useEffect(() => {
    (async () => {
      try {
        const existing = getStoredUserId();
        const user = await initUser(); // idempotent thanks to X-User-Id interceptor
        setMe(user);
        if (!existing) {
          toast.success(`Welcome, ${user.username}`);
        }
        await refreshAll();
      } catch (e) {
        console.error(e);
        toast.error("Failed to initialise user");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Paystack redirect (?reference=... or ?trxref=...)
  useEffect(() => {
    if (!me) return;
    const ref =
      searchParams.get("reference") || searchParams.get("trxref");
    if (!ref) return;

    (async () => {
      try {
        setVerifying(true);
        const res = await verifyReference(ref);
        if (res.status === "success") {
          toast.success(
            res.already_processed
              ? "Payment already credited"
              : "Payment successful — points credited"
          );
        } else {
          toast.error(`Payment ${res.status}`);
        }
        await refreshAll();
      } catch (e) {
        console.error(e);
        toast.error("Verification failed");
      } finally {
        // Strip query params so refresh doesn't re-verify
        const clean = new URLSearchParams();
        setSearchParams(clean, { replace: true });
        setVerifying(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const handleTopup = async (amount) => {
    try {
      setBusyAmount(amount);
      const { authorization_url } = await topupInit(amount);
      window.location.href = authorization_url;
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail || "Failed to start Paystack checkout"
      );
      setBusyAmount(null);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      {/* Global <AppToaster/> is mounted in App.js */}
      <div className="mx-auto max-w-5xl px-6 py-10">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <p
              className="text-xs uppercase tracking-[0.2em] text-neutral-500"
              data-testid="phase-label"
            >
              AfriTask AI · Phase 0
            </p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">
              Work Points POC
            </h1>
          </div>
          <Badge variant="outline" className="text-xs">
            Paystack Test Mode
          </Badge>
        </div>

        {/* Identity + Balance */}
        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-500">
                Anonymous user
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p
                className="font-mono text-lg font-semibold"
                data-testid="username-label"
              >
                {me?.username || "…"}
              </p>
              <p
                className="mt-1 truncate font-mono text-[11px] text-neutral-500"
                data-testid="user-id-label"
              >
                {me?.user_id || ""}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-500">
                Work Points
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p
                className="text-3xl font-semibold tabular-nums"
                data-testid="work-points-balance"
              >
                {wallet ? wallet.work_points.toLocaleString() : "—"}
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                Redeemable in-app currency
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-neutral-500">
                Earnings (NGN)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-semibold tabular-nums">
                {wallet ? fmtNaira(wallet.earnings_ngn) : "—"}
              </p>
              <p className="mt-1 text-xs text-neutral-500">
                Lifetime earned: {wallet ? fmtNaira(wallet.lifetime_earned) : "—"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Top-up buttons */}
        <div className="mt-10">
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">Top up Work Points</h2>
            {verifying && (
              <span className="text-xs text-neutral-500">
                Verifying last payment…
              </span>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {TOPUP_TIERS.map((t) => (
              <Card
                key={t.amount}
                className="transition-shadow hover:shadow-md"
              >
                <CardContent className="flex flex-col gap-3 p-5">
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-semibold">
                      {fmtNaira(t.amount)}
                    </p>
                    {t.badge && (
                      <Badge className="bg-emerald-600 hover:bg-emerald-600">
                        {t.badge}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-neutral-500">
                    Credits{" "}
                    <span className="font-medium text-neutral-900">
                      {t.points.toLocaleString()} pts
                    </span>
                  </p>
                  <Button
                    disabled={!me || busyAmount === t.amount}
                    onClick={() => handleTopup(t.amount)}
                    data-testid={`topup-btn-${t.amount}`}
                    className="w-full"
                  >
                    {busyAmount === t.amount
                      ? "Redirecting…"
                      : `Top up ${fmtNaira(t.amount)}`}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Separator className="my-10" />

        {/* Transactions */}
        <section>
          <h2 className="mb-3 text-lg font-semibold">Recent transactions</h2>
          <Card>
            <CardContent className="p-0">
              <Table data-testid="transactions-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Reference</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Points</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-8 text-center text-sm text-neutral-500"
                      >
                        No transactions yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {transactions.map((tx) => (
                    <TableRow key={tx.paystack_reference}>
                      <TableCell className="font-mono text-xs">
                        {tx.paystack_reference}
                      </TableCell>
                      <TableCell>{fmtNaira(tx.amount_ngn)}</TableCell>
                      <TableCell>
                        {tx.points_to_credit.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            tx.status === "success"
                              ? "default"
                              : tx.status === "failed"
                                ? "destructive"
                                : "secondary"
                          }
                        >
                          {tx.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {fmtWhen(tx.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>

        {/* Ledger */}
        <section className="mt-10">
          <h2 className="mb-3 text-lg font-semibold">Points ledger</h2>
          <Card>
            <CardContent className="p-0">
              <Table data-testid="ledger-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Δ</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Balance after</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ledger.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-8 text-center text-sm text-neutral-500"
                      >
                        Ledger is empty.
                      </TableCell>
                    </TableRow>
                  )}
                  {ledger.map((row, i) => (
                    <TableRow key={`${row.ref}-${i}`}>
                      <TableCell
                        className={
                          row.delta >= 0
                            ? "font-semibold text-emerald-700"
                            : "font-semibold text-red-700"
                        }
                      >
                        {row.delta >= 0 ? "+" : ""}
                        {row.delta.toLocaleString()}
                      </TableCell>
                      <TableCell>{row.reason}</TableCell>
                      <TableCell className="tabular-nums">
                        {row.balance_after.toLocaleString()}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.ref}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500">
                        {fmtWhen(row.timestamp)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </section>

        <p className="mt-10 text-center text-xs text-neutral-400">
          Use test card 4084 0840 8408 4081 · any future date · any CVV · OTP 123456
        </p>
      </div>
    </div>
  );
}
